import {
  ChangeDetectionStrategy,
  Component,
  computed,
  OnInit,
  Signal,
  signal,
  untracked,
  WritableSignal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, of, Subscription } from 'rxjs';
import { filter, finalize, switchMap, take, tap } from 'rxjs/operators';

import { DialogBase, SearchBarForm } from 'tiime-material';

import { ExonerationZoneApiService } from '@api-services/exoneration-zone-api.service';
import { ErrorCode, ErrorType } from '@constants/error.constant';
import { EXONERATION_ZONES, ExonerationZone } from '@constants/exoneration-zones.constant';
import { DateHelper } from '@helpers/date.helper';
import { FormHelper } from '@helpers/form.helper';
import { AppStoreState } from '@interfaces/app-store-state';
import { ErrorMessage } from '@interfaces/error-message';
import { Company } from '@models/company';
import { ExonerationZoneResult } from '@models/exoneration-zone-result';
import { companySelector } from '@modules/company/company-core/store/company/company-selector';
import { ColumnNames } from '@shared-dialogs/exoneration-zones-dialog/exoneration-zones-dialog.columns';

type Row = Omit<ExonerationZone, 'sites'> & {
  sites: Signal<string[]>;
  result: Signal<string>;
  resultPeriod: Signal<string | null>;
  hasResult: Signal<boolean>;
};

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-exoneration-zones-dialog',
  templateUrl: './exoneration-zones-dialog.component.html',
  styleUrls: ['./exoneration-zones-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExonerationZonesDialogComponent extends DialogBase<ExonerationZonesDialogComponent> implements OnInit {
  readonly searchForm: SearchBarForm = new SearchBarForm();
  readonly searchControl: FormControl<string> = this.searchForm.controls.search;
  readonly search: Signal<string> = toSignal(FormHelper.getValueChange<string>(this.searchControl));
  readonly rows: Row[];
  readonly ColumnNames: typeof ColumnNames = ColumnNames;
  readonly displayedColumns: ColumnNames[] = [
    ColumnNames.zone,
    ColumnNames.result,
    ColumnNames.period,
    ColumnNames.zoneReference,
    ColumnNames.site
  ];

  private readonly result: WritableSignal<ExonerationZoneResult> = signal(new ExonerationZoneResult());
  private readonly subscription: Subscription = new Subscription();

  constructor(
    private readonly store: Store<AppStoreState>,
    private readonly exonerationZoneApiService: ExonerationZoneApiService,
    dialogRef: MatDialogRef<ExonerationZonesDialogComponent>
  ) {
    super(dialogRef);
    this.searchControl.addValidators(Validators.pattern(/^[a-zA-Z\d]{5}$/));
    this.rows = this.getExonerationZones();
  }

  get searchErrorMessage(): ErrorMessage | null {
    if (!this.searchControl.value?.length) {
      return null;
    }
    return FormHelper.simpleErrorMessage(this.searchControl, {
      simpleErrorMessage: 'Le N°INSEE doit contenir 5 caractères alphanumériques',
      simpleError: ErrorCode.INVALID,
      simpleErrorCode: ErrorType.PATTERN
    });
  }

  ngOnInit(): void {
    this.observeSearch();

    this.subscription.add(
      this.store
        .select(companySelector)
        .pipe(
          filter((company: Company) => !!company),
          take(1),
          tap((company: Company) => {
            if (company.cityInseeCode?.length > 0) {
              this.searchControl.setValue(company.cityInseeCode);
            }
          })
        )
        .subscribe()
    );
  }

  private getExonerationZones(): Row[] {
    return EXONERATION_ZONES.map(
      (zone: ExonerationZone): Row => ({
        ...zone,
        sites: zone.key === 'qppv' ? this.getQppvSite(zone) : signal(zone.sites),
        result: this.getResult(zone),
        resultPeriod: this.getResultPeriod(zone),
        hasResult: this.getHasResult(zone)
      })
    );
  }

  private getResultPeriod(zone: ExonerationZone): Signal<string> {
    return computed(() => {
      const startDate = this.result()?.[zone.key]?.startDate;
      const endDate = this.result()?.[zone.key]?.endDate;
      if (!startDate && !endDate) {
        return null;
      }
      if (startDate && endDate) {
        return `Du ${DateHelper.format(startDate, 'DD/MM/YYYY')} au ${DateHelper.format(endDate, 'DD/MM/YYYY')}`;
      }
      if (startDate) {
        return `À partir du ${DateHelper.format(startDate, 'DD/MM/YYYY')}`;
      }

      return `Jusqu'au ${DateHelper.format(endDate, 'DD/MM/YYYY')}`;
    });
  }

  private getResult(zone: ExonerationZone): Signal<string> {
    return computed(() => {
      const search = untracked(() => this.search());
      const result = this.result();
      return result?.[zone.key]?.result ?? (search?.length ? 'Non éligible' : '');
    });
  }

  private getQppvSite(zone: ExonerationZone): Signal<string[]> {
    return computed(() => {
      const search = this.search();
      if (!search?.length) {
        return zone.sites;
      }
      return [`${zone.sites[0]}/territoire/${search}`];
    });
  }

  private getExonerationZoneResults(search: string): Observable<ExonerationZoneResult> {
    if (!search?.length) {
      return of(new ExonerationZoneResult());
    }
    this.isLoading.set(true);
    return this.exonerationZoneApiService
      .getExonerationZoneResult(search)
      .pipe(finalize(() => this.isLoading.set(false)));
  }

  private observeSearch(): void {
    this.subscription.add(
      FormHelper.getValueChange<string>(this.searchControl)
        .pipe(
          filter(() => this.searchControl.valid),
          switchMap((value: string) => this.getExonerationZoneResults(value)),
          tap((result: ExonerationZoneResult) => this.result.set(result))
        )
        .subscribe()
    );
  }

  private getHasResult(zone: ExonerationZone): Signal<boolean> {
    return computed(() => {
      const result = this.result()?.[zone.key]?.result;
      return result?.length > 0;
    });
  }
}
